import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { completeMagicLinkSignIn } from "@/lib/firebase";
import Navbar from "@/components/Navbar";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Dashboard from "@/pages/Dashboard";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/pages/AuthCallback";
import SlugRedirect from "@/pages/SlugRedirect";
import "@/App.css";

function AppShell({ children }) {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <Navbar />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-32 border-t border-border">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8 text-sm">
        <div>
          <div className="font-heading text-xl tracking-tighter font-medium mb-2">splink<span className="text-accent">.</span>pro</div>
          <p className="text-muted-foreground text-xs">Short URLs & QR codes for the SocialPulse network.</p>
        </div>
        <div>
          <div className="eyebrow text-muted-foreground mb-3">Product</div>
          <ul className="space-y-2 text-muted-foreground">
            <li><a href="/pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
            <li><a href="/dashboard" className="hover:text-foreground transition-colors">Dashboard</a></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-muted-foreground mb-3">Network</div>
          <ul className="space-y-2 text-muted-foreground text-xs font-mono">
            <li>SocialPulse</li><li>FoodPulse</li><li>IndianFamilyTree</li><li>NaturePulse</li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-muted-foreground mb-3">Legal</div>
          <p className="text-muted-foreground text-xs">© {new Date().getFullYear()} Splink.pro. Demo links expire after 7 days.</p>
        </div>
      </div>
    </footer>
  );
}

function AuthLinkHandler() {
  // Detect Firebase magic link on any page load and complete sign-in
  const { exchangeIdToken } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    (async () => {
      if (loc.search.includes("apiKey=") || loc.pathname === "/auth/callback") {
        try {
          const token = await completeMagicLinkSignIn();
          if (token) {
            await exchangeIdToken(token);
            nav("/dashboard", { replace: true });
          }
        } catch (e) {
          console.error("Magic link complete failed", e);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AuthLinkHandler />
          <Toaster position="top-right" />
          <Routes>
            <Route path="/" element={<AppShell><Landing /></AppShell>} />
            <Route path="/pricing" element={<AppShell><Pricing /></AppShell>} />
            <Route path="/dashboard" element={<AppShell><Dashboard /></AppShell>} />
            <Route path="/billing" element={<AppShell><Billing /></AppShell>} />
            <Route path="/billing/success" element={<AppShell><PaymentSuccess /></AppShell>} />
            <Route path="/billing/cancel" element={<AppShell><PaymentCancel /></AppShell>} />
            <Route path="/admin" element={<AppShell><Admin /></AppShell>} />
            <Route path="/auth/callback" element={<AppShell><AuthCallback /></AppShell>} />
            <Route path="/:slug" element={<SlugRedirect />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
